// Thin fetch wrapper for the visitor-facing Session API (design/2026-08-26-session-api-contract.md).
// The only file in src/presentation/ that talks to the network; embed.ts, question-flow.ts and
// result-screen.ts import from here rather than calling fetch directly, so the request/response
// shapes live in exactly one place and stay in lockstep with src/session/routes.ts. Every method
// takes a `baseUrl` (the embed's `config-endpoint` attribute) rather than assuming same-origin,
// since the embed always runs on a client site, never the Worker's own origin.

import type { ConfigVersion, Solution } from "../types";
import type { AlternateReason } from "../matching/types";
import type { PiiValue } from "../session/state";
import type { SessionCampaign } from "../campaign";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  outcome?: string;
  message?: string;
}

async function parseError(response: Response): Promise<never> {
  let body: ErrorBody = {};
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    // non-JSON error body; fall through with generic message
  }
  throw new ApiError(response.status, body.outcome ?? "unknown_error", body.message ?? response.statusText);
}

export async function fetchConfig(baseUrl: string): Promise<ConfigVersion> {
  const res = await fetch(`${baseUrl}/config`);
  if (!res.ok) return parseError(res);
  return (await res.json()) as ConfigVersion;
}

export interface StartSessionResult {
  sessionId: string;
  state: string;
  configVersionId: string;
  answers: { questionId: string; selectedOptionIds: string[]; isNoneOfThese: boolean; piiValue?: PiiValue }[];
  /** ADR-011: the question the adaptive planner has already decided comes next (ordinarily the
   * pinned opener; the same pending question read back on resume). `null` only if a session
   * somehow starts already complete. */
  nextQuestionId: string | null;
  /** Options this session offered for any optionsFromAttribute question, keyed by question id
   * (docs/plans/2026-10-08-options-from-attribute.md). Absent from an older Worker. */
  questionOptions?: Record<string, { id: string; label: string }[]>;
  /** docs/plans/2026-10-13-campaigns.md: present when the session started on a live campaign
   * page. Absent from an older Worker. */
  campaign?: SessionCampaign;
}

export async function startSession(
  baseUrl: string,
  configVersionId: string,
  turnstileToken: string,
  resumeSessionId?: string,
  /** True from the results screen's "Got another problem?" button; sent so telemetry can flag the
   * new session as a restart (migrations/0007_add_is_restart.sql). */
  restarted?: boolean,
  /** The host page's path, which the Worker matches against the Campaigns tab. */
  pagePath?: string
): Promise<StartSessionResult> {
  const res = await fetch(`${baseUrl}/session`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ configVersionId, turnstileToken, resumeSessionId, restarted: restarted || undefined, pagePath }),
  });
  if (!res.ok) return parseError(res);
  return (await res.json()) as StartSessionResult;
}

export interface RecordAnswerResult {
  state: string;
  isComplete: boolean;
  /** ADR-011: replaces the client computing the next question from `order` itself, which the
   * adaptive flow does not follow. `null` once the planner has decided to stop asking. */
  nextQuestionId: string | null;
  /** See StartSessionResult.questionOptions. */
  questionOptions?: Record<string, { id: string; label: string }[]>;
}

export async function recordAnswer(
  baseUrl: string,
  sessionId: string,
  questionId: string,
  selectedOptionIds: string[],
  isNoneOfThese: boolean,
  piiValue?: PiiValue
): Promise<RecordAnswerResult> {
  const res = await fetch(`${baseUrl}/session/${sessionId}/answers`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ questionId, selectedOptionIds, isNoneOfThese, piiValue }),
  });
  if (!res.ok) return parseError(res);
  return (await res.json()) as RecordAnswerResult;
}

export interface OptOutResult {
  state: string;
  findABoardMemberUrl: string;
}

export async function optOut(baseUrl: string, sessionId: string): Promise<OptOutResult> {
  const res = await fetch(`${baseUrl}/session/${sessionId}/opt-out`, { method: "POST" });
  if (!res.ok) return parseError(res);
  return (await res.json()) as OptOutResult;
}

// --- /complete (SSE) -----------------------------------------------------------------------

export interface ResultPayload {
  outcome: string;
  confidence: number | null;
  chosenSolutionId: string | null;
  rankedSolutionIds: string[];
  /** Reasons for the alternates that render, in display order. Empty on a fresh /complete: they
   * come afterwards from `fetchAlternates` (2026-09-25 plan). Only populated when /complete
   * replays a session whose alternates call already finished. May be shorter than the rendered
   * alternates (the model skipped one), and is absent from a server predating this field. */
  alternateReasons?: AlternateReason[];
  solution: (Pick<Solution, "name" | "oneLiner" | "bookingUrl"> & { attributes: Record<string, string> }) | null;
}

export interface CompleteHandlers {
  onReflectedProblem: (text: string) => void;
  onReasoning: (text: string) => void;
  onResult: (result: ResultPayload) => void;
}

/**
 * Parses the `event:`/`data:` SSE frames the contract specifies (reflected_problem, reasoning,
 * result, error), invoking the matching handler as each frame completes. No `EventSource`: this is
 * a POST with no body, which `EventSource` cannot issue, and the repo has no SSE client dependency
 * to pull in for one route.
 */
export async function complete(baseUrl: string, sessionId: string, handlers: CompleteHandlers): Promise<void> {
  const res = await fetch(`${baseUrl}/session/${sessionId}/complete`, {
    method: "POST",
    headers: { accept: "text/event-stream" },
  });
  if (!res.ok || !res.body) return parseError(res);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (event: string, data: string) => {
    switch (event) {
      case "reflected_problem":
        handlers.onReflectedProblem((JSON.parse(data) as { text: string }).text);
        break;
      case "reasoning":
        handlers.onReasoning((JSON.parse(data) as { text: string }).text);
        break;
      case "result":
        handlers.onResult(JSON.parse(data) as ResultPayload);
        break;
      case "error":
        throw new ApiError(0, "stream_error", (JSON.parse(data) as { message: string }).message);
      default:
        break;
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let frameEnd: number;
    while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      parseFrame(frame, dispatch);
    }
  }
}

/**
 * POST /session/:id/alternates (2026-09-25 plan): the second model call's reasons for the
 * alternates shown behind the top match. Called by the result screen once the top match is on
 * screen. The server answers `[]` rather than an error for every non-clean outcome, so a thrown
 * ApiError here means a transport or session problem, which the caller treats the same way.
 */
export async function fetchAlternates(baseUrl: string, sessionId: string): Promise<AlternateReason[]> {
  const res = await fetch(`${baseUrl}/session/${sessionId}/alternates`, { method: "POST" });
  if (!res.ok) return parseError(res);
  return ((await res.json()) as { alternateReasons: AlternateReason[] }).alternateReasons;
}

function parseFrame(frame: string, dispatch: (event: string, data: string) => void): void {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice("event:".length).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice("data:".length).trim());
  }
  if (dataLines.length === 0) return;
  dispatch(event, dataLines.join("\n"));
}
